import { z } from 'zod';

/* ─────────── Нормативная база РК и нормативная библиотека (разделы 6.5 и 6.22, поток 2) ─────────── */

const Money = z.number().finite().nullable();

/** Виды документов нормативной базы (СМ-01, БЗ-12); справочник пополняет администратор */
export const NORM_KINDS = {
  methodology: 'Методика (НДЦС)',
  esn: 'ЭСН — сметные нормы',
  ssc: 'ССЦ — сметные цены на материалы и оборудование',
  scem: 'СЦЭМ — сметные цены на эксплуатацию машин',
  sczt: 'СЦЗТ — сметные цены на затраты труда',
  scpg: 'СЦПГ — сметные цены на перевозки грузов',
  index: 'Индексы НДЦС РК 8.04-07',
  upss: 'УПСС — укрупнённые показатели',
  esc: 'ЕСЦ — единичные сметные цены',
  agsk: 'АГСК — перечни и классификатор ресурсов',
  pricelist: 'Прайс-лист и конъюнктурный анализ цен',
  sn: 'СН РК / СП РК — нормы проектирования',
  sp_en: 'СП РК EN — еврокоды и национальные приложения',
  snip: 'СНиП, МСН, РДС — прежние нормы',
  ntp: 'НТП — нормы технологического проектирования',
  scp: 'СЦП / СЦИ — цены на проектные работы и изыскания',
  nztp: 'НЗТП — нормы затрат труда на проектирование',
  techmap: 'Технологическая карта',
  gost: 'ГОСТ, СТ РК — стандарты',
  law: 'Закон, кодекс',
  act: 'Подзаконный акт, приказ',
  other: 'Другой документ',
} as const;
export type NormKind = keyof typeof NORM_KINDS;
export const NormKindSchema = z.enum(Object.keys(NORM_KINDS) as [NormKind, ...NormKind[]]);

/** Статус действия документа (БЗ-13) */
export const NORM_STATUSES = {
  active: 'Действует',
  replaced: 'Заменён',
  inactive: 'Не действует',
  check: 'Требует сверки',
} as const;
export type NormStatus = keyof typeof NORM_STATUSES;
export const NormStatusSchema = z.enum(Object.keys(NORM_STATUSES) as [NormStatus, ...NormStatus[]]);

/** Виды ресурсов классификатора (АГСК-3) */
export const RESOURCE_KINDS = {
  labor: 'Труд рабочих',
  machinist: 'Труд машинистов',
  material: 'Материал',
  machine: 'Машина',
  equipment: 'Оборудование',
} as const;
export type NormResourceKind = keyof typeof RESOURCE_KINDS;

export const NormReleaseInput = z.object({
  code: z.string().trim().min(2).max(120),
  name: z.string().trim().min(2).max(500),
  nameKk: z.string().trim().max(500).nullable().optional(),
  kind: NormKindSchema,
  year: z.number().int().min(1990).max(2100).nullable().optional(),
  issue: z.string().trim().max(60).nullable().optional(),
  priceZone: z.string().trim().max(40).nullable().optional(),
  priceLevel: z.string().trim().max(120).nullable().optional(),
  orderNo: z.string().trim().max(200).nullable().optional(),
  effectiveFrom: z.iso.date().nullable().optional(),
  replacesId: z.uuid().nullable().optional(),
  status: NormStatusSchema.optional(),
  statusSource: z.string().trim().max(500).nullable().optional(),
  languages: z.array(z.enum(['ru', 'kk'])).optional(),
  note: z.string().trim().max(4000).nullable().optional(),
});
export type NormReleaseInput = z.infer<typeof NormReleaseInput>;

export const NormReleaseSummary = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  nameKk: z.string().nullable(),
  kind: NormKindSchema,
  year: z.number().nullable(),
  issue: z.string().nullable(),
  priceZone: z.string().nullable(),
  priceLevel: z.string().nullable(),
  orderNo: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  replacesId: z.uuid().nullable(),
  replacesCode: z.string().nullable(),
  replacedById: z.uuid().nullable(),
  replacedByCode: z.string().nullable(),
  status: NormStatusSchema,
  statusSource: z.string().nullable(),
  languages: z.array(z.string()),
  note: z.string().nullable(),
  items: z.number().int(),
  resources: z.number().int(),
  /** Сколько смет посчитано по этому выпуску (БЗ-17) */
  estimates: z.number().int(),
  updatedAt: z.string(),
});
export type NormReleaseSummary = z.infer<typeof NormReleaseSummary>;

export const NormItemRow = z.object({
  id: z.uuid(),
  releaseId: z.uuid(),
  releaseCode: z.string(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  costKind: z.string(),
  collection: z.string().nullable(),
  source: z.string().nullable(),
  usageCount: z.number().int(),
  /** Прямые затраты на измеритель по ценам выпуска (без коэффициентов); null без доступа к суммам */
  unitDirect: Money,
  laborHours: z.number().nullable(),
});
export type NormItemRow = z.infer<typeof NormItemRow>;

export const NormResourceRow = z.object({
  id: z.uuid(),
  releaseId: z.uuid(),
  releaseCode: z.string(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  kind: z.string(),
  price: Money,
  machinistPay: Money,
  basePrice: Money,
  mass: z.number().nullable(),
  grade: z.number().nullable(),
  source: z.string().nullable(),
});
export type NormResourceRow = z.infer<typeof NormResourceRow>;

export const NormItemCard = NormItemRow.extend({
  resources: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      kind: z.string(),
      unit: z.string(),
      norm: z.number(),
      price: Money,
      amount: Money,
    }),
  ),
  /** История цен нормы по выпускам (СМ-03) */
  history: z.array(
    z.object({
      releaseId: z.uuid(),
      releaseCode: z.string(),
      year: z.number().nullable(),
      unitDirect: Money,
    }),
  ),
  /** Аналоги: нормы той же таблицы сборника */
  analogs: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string(), unit: z.string() })),
  /** Где применена (БЗ-17) */
  usedIn: z.array(
    z.object({
      estimateId: z.uuid(),
      estimateCode: z.string(),
      estimateName: z.string(),
      projectId: z.uuid(),
      projectCode: z.string(),
      items: z.number().int(),
    }),
  ),
});
export type NormItemCard = z.infer<typeof NormItemCard>;

export const NormSearchResult = z.object({
  items: z.array(NormItemRow),
  resources: z.array(NormResourceRow),
  total: z.number().int(),
});
export type NormSearchResult = z.infer<typeof NormSearchResult>;

export const NormsOverview = z.object({
  releases: z.array(NormReleaseSummary),
  /** Документы без подтверждённого статуса: в новые сметы не подставляются (БЗ-13) */
  needCheck: z.number().int(),
  /** Недостающие документы из приложения А ТЗ (БЗ-18) */
  missing: z.array(z.object({ code: z.string(), what: z.string(), owner: z.string() })),
});
export type NormsOverview = z.infer<typeof NormsOverview>;

export const NormImportResult = z.object({
  releases: z.array(
    z.object({
      id: z.uuid(),
      code: z.string(),
      created: z.boolean(),
      items: z.number().int(),
      resources: z.number().int(),
    }),
  ),
  itemsAdded: z.number().int(),
  resourcesAdded: z.number().int(),
  skipped: z.number().int(),
  issues: z.array(z.string()),
});
export type NormImportResult = z.infer<typeof NormImportResult>;

/**
 * Недостающие документы по приложению А.4 ТЗ на 16.10.2026. Список ведётся в коде до появления
 * загрузки с Диска (БЗ-14); ответственный за поиск первоисточника — сметный отдел.
 */
export const MISSING_NORM_DOCS: Array<{ code: string; what: string; owner: string }> = [
  {
    code: 'ССЦ РК 8.04-08-2026',
    what: 'Книги 3 и 4 и казахская версия; до получения Книги 3 и 4 редакции 2025 года — справочно',
    owner: 'Сметный отдел',
  },
  { code: 'ССЦ РК 8.04-09-2026', what: 'Казахская версия', owner: 'Сметный отдел' },
  { code: 'УСН РК 8.02-04-2025', what: 'Казахская версия', owner: 'Сметный отдел' },
  { code: 'ЭСН РК 8.04-01-2024', what: 'Казахские сборники 1116 и 1146', owner: 'Сметный отдел' },
  {
    code: 'СП РК EN 1990, EN 1992-1-1',
    what: 'Основной текст и национальные приложения; без приложений лежат EN 1993-3-1 и EN 1997-2',
    owner: 'ГИП',
  },
  {
    code: 'Приказ № 59-нқ от 01.06.2026',
    what: 'Первоисточник приказа о KENML 2.01 (на Диске только СП РК 1.02-123-2025 с приказом № 164)',
    owner: 'Сметный отдел',
  },
  { code: 'Изменения и дополнения к ЭСН', what: 'Выпуски 2–8, 14 и 37', owner: 'Сметный отдел' },
  { code: 'НЗТП РК 8.03-01-2016', what: 'Разделы 2, 22, 41, 45, 46, 48 и 51–54', owner: 'Сметный отдел' },
  {
    code: 'АГСК-5',
    what: 'Текущий выпуск перечня документов по ценообразованию (на Диске — на 05.04.2024)',
    owner: 'Сметный отдел',
  },
];

/* ---------- Нормативная библиотека: загрузка с Диска, перечни АГСК, список на проверку (БЗ-14, БЗ-15, БЗ-18) ---------- */

export const LIBRARY_ROW_RESULTS = {
  loaded: 'Загружен',
  duplicate: 'Копия',
  wrapper: 'HTML-обёртка',
  archive: 'Архив',
  skipped: 'Пропущен',
  review: 'На проверку',
  error: 'Ошибка',
} as const;
export type LibraryRowResult = keyof typeof LIBRARY_ROW_RESULTS;
export const LibraryRowResultSchema = z.enum(
  Object.keys(LIBRARY_ROW_RESULTS) as [LibraryRowResult, ...LibraryRowResult[]],
);

export const LIBRARY_IMPORT_STATUSES = {
  queued: 'В очереди',
  running: 'Идёт',
  done: 'Закончена',
  failed: 'Сбой',
  cancelled: 'Остановлена',
} as const;
export type LibraryImportStatus = keyof typeof LIBRARY_IMPORT_STATUSES;

export const LibraryTotals = z.object({
  files: z.number().int().default(0),
  loaded: z.number().int().default(0),
  duplicate: z.number().int().default(0),
  wrapper: z.number().int().default(0),
  archive: z.number().int().default(0),
  skipped: z.number().int().default(0),
  review: z.number().int().default(0),
  error: z.number().int().default(0),
  bytes: z.number().default(0),
  releases: z.number().int().default(0),
});
export type LibraryTotals = z.infer<typeof LibraryTotals>;

export const LibraryImport = z.object({
  id: z.uuid(),
  source: z.enum(['drive', 'upload', 'agsk']),
  folderId: z.string().nullable(),
  folderName: z.string().nullable(),
  status: z.enum(Object.keys(LIBRARY_IMPORT_STATUSES) as [LibraryImportStatus, ...LibraryImportStatus[]]),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  totals: LibraryTotals,
  progress: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
});
export type LibraryImport = z.infer<typeof LibraryImport>;

/** Что разобрано с титула файла (БЗ-14) */
export const LibraryDetected = z.object({
  code: z.string().nullable().optional(),
  codeKk: z.string().nullable().optional(),
  kind: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  year: z.number().nullable().optional(),
  issue: z.string().nullable().optional(),
  orderNo: z.string().nullable().optional(),
  orderDate: z.string().nullable().optional(),
  effectiveFrom: z.string().nullable().optional(),
  replaces: z.array(z.string()).optional(),
  languages: z.array(z.string()).optional(),
  region: z.string().nullable().optional(),
  source: z.enum(['title', 'filename', 'none']).optional(),
  warnings: z.array(z.string()).optional(),
  innerName: z.string().nullable().optional(),
});
export type LibraryDetected = z.infer<typeof LibraryDetected>;

export const LibraryImportRow = z.object({
  id: z.uuid(),
  path: z.string(),
  name: z.string(),
  driveFileId: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  sha256: z.string().nullable(),
  result: LibraryRowResultSchema,
  message: z.string().nullable(),
  duplicateOf: z.string().nullable(),
  releaseId: z.uuid().nullable(),
  releaseCode: z.string().nullable(),
  fileId: z.uuid().nullable(),
  detected: LibraryDetected,
  createdAt: z.string(),
});
export type LibraryImportRow = z.infer<typeof LibraryImportRow>;

export const LibraryImportDetail = z.object({
  import: LibraryImport,
  rows: z.array(LibraryImportRow),
  total: z.number().int(),
});
export type LibraryImportDetail = z.infer<typeof LibraryImportDetail>;

/** Состояние загрузки с Диска: настроен ли ключ, какая папка, адрес сервисного аккаунта для доступа */
export const LibraryDriveStatus = z.object({
  configured: z.boolean(),
  accountEmail: z.string().nullable(),
  folderId: z.string().nullable(),
  /** Папку может задать владелец компании или администратор портала */
  canSetFolder: z.boolean(),
  running: LibraryImport.nullable(),
});
export type LibraryDriveStatus = z.infer<typeof LibraryDriveStatus>;

export const AGSK_LISTS = {
  agsk1: 'АГСК-1 — нормативные правовые акты и нормативно-технические документы',
  agsk4: 'АГСК-4 — технологии производства работ',
  agsk5: 'АГСК-5 — документы по ценообразованию',
} as const;
export type AgskList = keyof typeof AGSK_LISTS;
export const AgskListSchema = z.enum(Object.keys(AGSK_LISTS) as [AgskList, ...AgskList[]]);

export const AgskEntry = z.object({
  id: z.uuid(),
  list: AgskListSchema,
  listDate: z.string().nullable(),
  section: z.string().nullable(),
  code: z.string(),
  codeKk: z.string().nullable(),
  codes: z.array(z.string()),
  name: z.string(),
  nameKk: z.string().nullable(),
  approved: z.string().nullable(),
  orderNo: z.string().nullable(),
  orderDate: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  replaces: z.array(z.string()),
  amended: z.array(z.string()),
  asOf: z.string().nullable(),
  mks: z.string().nullable(),
  /** Выпуски библиотеки с тем же обозначением */
  releaseIds: z.array(z.uuid()),
});
export type AgskEntry = z.infer<typeof AgskEntry>;

export const AgskImportResult = z.object({
  list: AgskListSchema,
  listDate: z.string().nullable(),
  entries: z.number().int(),
  replaced: z.number().int(),
  warnings: z.array(z.string()),
  /** Итог сверки библиотеки с перечнем после загрузки */
  check: z.object({
    checked: z.number().int(),
    confirmed: z.number().int(),
    reviews: z.number().int(),
  }),
});
export type AgskImportResult = z.infer<typeof AgskImportResult>;

export const AgskOverview = z.object({
  lists: z.array(
    z.object({
      list: AgskListSchema,
      title: z.string(),
      listDate: z.string().nullable(),
      entries: z.number().int(),
      loadedAt: z.string().nullable(),
    }),
  ),
});
export type AgskOverview = z.infer<typeof AgskOverview>;

export const LIBRARY_REVIEW_KINDS = {
  not_in_agsk: 'Нет в перечне АГСК',
  newer_than_agsk: 'Редакция позже перечня',
  replaced_in_agsk: 'В перечне заменён',
  series_conflict: 'Один номер, разные годы',
  title_mismatch: 'Титул не совпал с именем файла',
  no_title: 'Обозначение не найдено',
  wrapper: 'Обёртка без файла',
  archive: 'Архив не распакован',
  missing: 'Документа нет на Диске',
} as const;
export type LibraryReviewKind = keyof typeof LIBRARY_REVIEW_KINDS;
export const LibraryReviewKindSchema = z.enum(
  Object.keys(LIBRARY_REVIEW_KINDS) as [LibraryReviewKind, ...LibraryReviewKind[]],
);

export const REVIEW_OWNERS = { estimators: 'Сметный отдел', gip: 'ГИП' } as const;
export type ReviewOwner = keyof typeof REVIEW_OWNERS;

export const LibraryReview = z.object({
  id: z.uuid(),
  kind: LibraryReviewKindSchema,
  code: z.string().nullable(),
  message: z.string(),
  owner: z.enum(['estimators', 'gip']),
  releaseId: z.uuid().nullable(),
  releaseCode: z.string().nullable(),
  importRowId: z.uuid().nullable(),
  agskEntryId: z.uuid().nullable(),
  status: z.enum(['open', 'resolved']),
  resolution: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type LibraryReview = z.infer<typeof LibraryReview>;

export const LibraryReviewPatch = z.object({
  status: z.enum(['open', 'resolved']),
  resolution: z.string().trim().max(2000).nullable().optional(),
  /** Статус документа по решению сметного отдела (БЗ-13): источник статуса — «заметка сметного отдела» */
  releaseStatus: NormStatusSchema.optional(),
});
export type LibraryReviewPatch = z.infer<typeof LibraryReviewPatch>;

export const LibraryCheckResult = z.object({
  checked: z.number().int(),
  confirmed: z.number().int(),
  replaced: z.number().int(),
  reviews: z.number().int(),
});
export type LibraryCheckResult = z.infer<typeof LibraryCheckResult>;

/** Файл документа библиотеки (карточка выпуска) */
export const LibraryFile = z.object({
  id: z.uuid(),
  name: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  path: z.string().nullable(),
  languages: z.array(z.string()),
  createdAt: z.string(),
});
export type LibraryFile = z.infer<typeof LibraryFile>;

/** Класс классификатора строительных ресурсов АГСК-3 для каталога ТМЦ склада (СК-02, БЗ-15): код и название, без цен */
export const AgskClassRow = z.object({
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  /** labor, machinist, material, machine, equipment */
  kind: z.string(),
  /** Группа: первый сегмент кода («216» из «216-103-0101») */
  group: z.string(),
  /** Сколько выпусков базы содержат этот код */
  releases: z.number().int(),
});
export type AgskClassRow = z.infer<typeof AgskClassRow>;
export const AgskClassList = z.object({ rows: z.array(AgskClassRow), total: z.number().int() });
export type AgskClassList = z.infer<typeof AgskClassList>;
